import React from "react";

/**
 * Heritage logos, PNGs with proper alpha (cream backgrounds stripped).
 * They drop onto any page background cleanly.
 *
 *  - Default → dark linework (use on light/cream pages).
 *  - onDark  → invert filter so the same artwork shows in light ink (use on
 *              charcoal / dark imagery).
 */

export function ApparelLogo({ className = "", onDark = false, style, ...rest }) {
  return (
    <img
      src="/brand/apparel-logo.png"
      alt="Heritage Apparel"
      draggable="false"
      className={`block select-none ${className}`}
      style={onDark ? { filter: "invert(1)", ...style } : style}
      {...rest}
    />
  );
}

export function DahliasLogo({ className = "", onDark = false, variant = "line", style, ...rest }) {
  // variant: "seal" (new round purple+orange badge), "line" (older linework), "sage" (sage badge)
  let src;
  if (variant === "seal") src = "/photos/brand/heritage-seal.png";
  else if (variant === "sage") src = "/brand/dahlias-badge-sage.png";
  else src = "/brand/dahlias-badge.png";
  const filter = onDark && (variant === "line") ? "invert(1)" : undefined;
  return (
    <img
      src={src}
      alt="Heritage Dahlias · Est. 1932"
      draggable="false"
      className={`block select-none ${className}`}
      style={filter ? { filter, ...style } : style}
      {...rest}
    />
  );
}

/** New flagship seal (purple HERITAGE DAHLIAS with orange wavy frame). */
export function HeritageSeal({ className = "", style, ...rest }) {
  return (
    <img
      src="/photos/brand/heritage-seal.png"
      alt="Heritage Dahlias · Est. 1932"
      draggable="false"
      className={`block select-none ${className}`}
      style={style}
      {...rest}
    />
  );
}

/**
 * Silhouette version of the seal, solid one-colour cut-out using mask-image.
 * Default colour is forest green; pass `color` for any other.
 * Use this where the full polychrome seal is too busy (small/inline uses).
 */
export function HeritageSealSilhouette({ className = "", color = "var(--dahlias-forest, #2F4A2A)", style, ...rest }) {
  const mask = {
    WebkitMaskImage: "url(/photos/brand/heritage-seal.png)",
    maskImage: "url(/photos/brand/heritage-seal.png)",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskPosition: "center",
    maskPosition: "center",
    WebkitMaskSize: "contain",
    maskSize: "contain",
    backgroundColor: color,
  };
  return (
    <span
      role="img"
      aria-label="Heritage Dahlias"
      className={`inline-block ${className}`}
      style={{ ...mask, ...style }}
      {...rest}
    />
  );
}

/**
 * Plain text wordmark for places where the seal doesn't sit well
 * (e.g. tight nav bars, mobile top-bar, in-line in copy).
 * Caps, nice serif, generous letter-spacing.
 */
export function HeritageWordmark({ className = "", as: Tag = "span", italic = false, ...rest }) {
  return (
    <Tag
      className={`font-display tracking-[0.18em] uppercase text-[var(--dahlias-forest,#2F4A2A)] ${italic ? "italic" : ""} ${className}`}
      {...rest}
    >
      Heritage Dahlias
    </Tag>
  );
}

export function HeritageMasterLogo({ className = "", onDark = false, style, ...rest }) {
  return (
    <img
      src="/brand/heritage-master.png"
      alt="Heritage"
      draggable="false"
      className={`block select-none ${className}`}
      style={onDark ? { filter: "invert(1)", ...style } : style}
      {...rest}
    />
  );
}
